import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LocaleProvider } from "@/i18n/LocaleContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import Index from "./pages/Index";
import MaintenanceGate from "./components/MaintenanceGate";
import LanguageRedirect from "./components/LanguageRedirect";
import GAPageViewTracker from "./components/GAPageViewTracker";
import useCalendlyTracking from "./hooks/useCalendlyTracking";

const LazyToaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const LazySonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const SobreNosotros = lazy(() => import("./pages/SobreNosotros"));
const EspacioInformativo = lazy(() => import("./pages/EspacioInformativo"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPostForm = lazy(() => import("./pages/admin/AdminPostForm"));
const AvisoLegal = lazy(() => import("./pages/AvisoLegal"));
const PoliticaPrivacidad = lazy(() => import("./pages/PoliticaPrivacidad"));
const PoliticaCookies = lazy(() => import("./pages/PoliticaCookies"));
const CitaConfirmada = lazy(() => import("./pages/CitaConfirmada"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RegularizacionDenegada = lazy(() => import("./pages/RegularizacionDenegada"));
const RegularizationDeniedEN = lazy(() => import("./pages/RegularizationDeniedEN"));
const RegularisationRefuseeFR = lazy(() => import("./pages/RegularisationRefuseeFR"));

const queryClient = new QueryClient();

const CalendlyTrackingMount = () => {
  useCalendlyTracking();
  return null;
};

const AppRoutes = () => (
  <LocaleProvider>
    <GAPageViewTracker />
    <CalendlyTrackingMount />
    <MaintenanceGate>
      <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          {/* Language auto-detect (only on /) */}
          <Route path="/" element={<><LanguageRedirect /><Index /></>} />
          <Route path="/sobre-nosotros" element={<SobreNosotros />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
          <Route path="/espacio-informativo" element={<EspacioInformativo />} />
          <Route path="/espacio-informativo/regularizacion-denegada-que-hacer" element={<RegularizacionDenegada />} />
          {/* Legacy slug redirects (recover SEO equity from zombie URLs) */}
          <Route
            path="/espacio-informativo/regularizacion-masiva-2026-guia-completa"
            element={<Navigate to="/espacio-informativo/regularizacion-masiva-2026-guia-completa-requisitos-plazos" replace />}
          />
          <Route
            path="/espacio-informativo/spain-mass-regularization-2026-complete-guide"
            element={<Navigate to="/en/resources/spain-mass-regularization-2026-complete-guide" replace />}
          />
          <Route
            path="/espacio-informativo/do-you-qualify-spain-amnesty-2026-requirements"
            element={<Navigate to="/en/resources/do-you-qualify-spain-amnesty-2026-requirements" replace />}
          />
          <Route
            path="/espacio-informativo/spain-regularization-2026-7-mistakes-application-denied"
            element={<Navigate to="/en/resources/spain-regularization-2026-7-mistakes-application-denied" replace />}
          />
          <Route
            path="/espacio-informativo/criminal-record-spain-regularization-2026"
            element={<Navigate to="/en/resources/criminal-record-spain-regularization-2026" replace />}
          />
          <Route
            path="/espacio-informativo/vulnerability-certificate-spain-regularization-2026"
            element={<Navigate to="/en/resources/vulnerability-certificate-spain-regularization-2026" replace />}
          />
          <Route path="/espacio-informativo/:slug" element={<BlogPost />} />
          <Route path="/cita-confirmada" element={<CitaConfirmada />} />

          {/* Legacy redirect */}
          <Route path="/blog/regularizacion-denegada-que-hacer" element={<Navigate to="/espacio-informativo/regularizacion-denegada-que-hacer" replace />} />

          {/* English routes */}
          <Route path="/en" element={<Index />} />
          <Route path="/en/about-us" element={<SobreNosotros />} />
          <Route path="/en/legal-notice" element={<AvisoLegal />} />
          <Route path="/en/privacy-policy" element={<PoliticaPrivacidad />} />
          <Route path="/en/cookie-policy" element={<PoliticaCookies />} />
          <Route path="/en/resources" element={<EspacioInformativo />} />
          <Route path="/en/resources/regularization-denied-what-to-do" element={<RegularizationDeniedEN />} />
          <Route path="/en/resources/:slug" element={<BlogPost />} />
          <Route path="/en/booking-confirmed" element={<CitaConfirmada />} />

          {/* French routes */}
          <Route path="/fr" element={<Index />} />
          <Route path="/fr/a-propos" element={<SobreNosotros />} />
          <Route path="/fr/mentions-legales" element={<AvisoLegal />} />
          <Route path="/fr/politique-confidentialite" element={<PoliticaPrivacidad />} />
          <Route path="/fr/politique-cookies" element={<PoliticaCookies />} />
          <Route path="/fr/ressources" element={<EspacioInformativo />} />
          <Route path="/fr/ressources/regularisation-refusee-que-faire" element={<RegularisationRefuseeFR />} />
          <Route path="/fr/ressources/:slug" element={<BlogPost />} />
          <Route path="/fr/rendez-vous-confirme" element={<CitaConfirmada />} />

          {/* Admin routes (no locale) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/posts/nuevo"
            element={
              <ProtectedRoute>
                <AdminPostForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/posts/:id/editar"
            element={
              <ProtectedRoute>
                <AdminPostForm />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </MaintenanceGate>
  </LocaleProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}><LazyToaster /></Suspense>
      <Suspense fallback={null}><LazySonner /></Suspense>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
